import { ChangeEvent, useEffect, useState } from "react";
import { StatusSearchInputs } from "../../pages/support/alarm/StatusPage";
import { SelectOption } from "../types/selectOption";
import { useQuery } from "@tanstack/react-query";
import { request } from "../../common/api";
import {
  GET_LOW_MACHINERY_SELECT_OPTIONS,
  GET_MACHINERY_SELECT_OPTIONS,
  GET_SENSOR_SELECT_OPTIONS,
} from "../query-keys";

const useStatusSearchInputData = (searchDefaultValues: StatusSearchInputs) => {
  const [search, setSearch] = useState<StatusSearchInputs>(searchDefaultValues);

  // 장비 options 조회
  const { data: machinerySelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_MACHINERY_SELECT_OPTIONS, search.steShipNo],
    queryFn: async () => {
      if (!search.steShipNo) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/equipment-info/machinery/select/options/${search.steShipNo}`,
      });
    },
  });

  // 하위장비 options 조회
  const { data: lowMachinerySelectOptions } = useQuery<SelectOption[]>({
    queryKey: [GET_LOW_MACHINERY_SELECT_OPTIONS, search.steMcyName],
    queryFn: async () => {
      if (!search.steMcyName) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/equipment-info/low-machinery/select/options/${search.steMcyName}`,
      });
    },
  });

  // 센서 options 조회
  const { data: sensorSelectOptions } = useQuery<SelectOption[]>({
    queryKey: [
      GET_SENSOR_SELECT_OPTIONS,
      search.steLowMcyName,
      search.ssrSignal,
    ],
    queryFn: async () => {
      if (!search.steLowMcyName) {
        return [];
      }

      return await request<SelectOption[]>({
        method: "GET",
        url: `/sensor/select/options`,
        params: {
          ssrShipNo: search.steShipNo,
          ssrMcyName: search.steMcyName,
          ssrLowMcyName: search.steLowMcyName,
          ssrSignal: search.ssrSignal,
        },
      });
    },
  });

  // 장비 선택 변경
  const handleMcySelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      steMcyName: e.target.options[e.target.selectedIndex].text,
      ssrMachine: e.target.value,
    });
  };

  // 하위장비 선택 변경
  const handleLowMcySelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      steLowMcyName: e.target.value,
    });
  };

  // 센서 선택 변경
  const handleSensorSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      steSensorName: e.target.value,
    });
  };

  // 알람 레벨 선택 변경
  const handleAlarmSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearch({
      ...search,
      steAlarmType: e.target.value,
    });
  };

  // 센서 값 및 날짜 변경
  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setSearch({
      ...search,
      [name]: value,
    });
  };
  // 상태변경 자유 함수
  const setSearchData = (key: string, value: any) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      [key]: value,
    }));
  };
  // searchDefaultValues 변경 시 마다 search 초기화

  useEffect(() => {
    setSearch(searchDefaultValues);
  }, [searchDefaultValues.steShipNo]);

  return {
    search,

    /** 장비 select options */
    machinerySelectOptions,

    /** 하위장비 select options */
    lowMachinerySelectOptions,

    /** 센서 select options */
    sensorSelectOptions,

    /** 장비 선택 변경 */
    handleMcySelectChange,

    /** 하위장비 선택 변경 */
    handleLowMcySelectChange,

    /** 센서 선택 변경 */
    handleSensorSelectChange,

    /** 알람 레벨 선택 변경 */
    handleAlarmSelectChange,

    /** 센서 값 및 날짜 변경*/
    handleValueChange,
    setSearchData,
    setSearch,
  };
};

export default useStatusSearchInputData;
